import { useEffect, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'

type ConfirmationDialogProps = Omit<DialogProps, 'children'> & {
  title?: string
  description?: string
  isLoading?: boolean
  cancelCta?: string
  confirmCta?: string
  topContent?: React.ReactNode
  mainContent?: React.ReactNode
  hasTickConfirmation?: boolean
  tickConfirmationText?: string
  hasCloseButton?: boolean
  onCancel?: () => void
  onConfirm?: () => void
  onClose?: () => void
}

export function ConfirmationDialog({
  title = 'Apakah Anda yakin ingin melanjutkan?',
  description,
  isLoading,
  cancelCta = 'Tidak',
  confirmCta = 'Ya',
  topContent,
  mainContent,
  hasTickConfirmation,
  tickConfirmationText = 'Saya yakin ingin melanjutkan',
  hasCloseButton = true,
  isOpen,
  onCancel,
  onConfirm,
  onClose,
  ...dialogProps
}: ConfirmationDialogProps) {
  // ----------------------------------------------------
  // Handle confirmation
  // ----------------------------------------------------
  const [isConfirmed, setIsConfirmed] = useState(false)

  // Reset upon close
  useEffect(() => {
    if (hasTickConfirmation) {
      if (!isOpen) setIsConfirmed(false)
    } else {
      setIsConfirmed(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasTickConfirmation])

  return (
    <Dialog size="sm" isOpen={isOpen} {...dialogProps}>
      <DialogOverlay />
      <DialogContent
        aria-labelledby={`confirmation-dialog-title`}
        aria-describedby={`confirmation-dialog-description`}
      >
        {hasCloseButton && (
          <DialogCloseButton aria-label="Tutup" onClick={onClose} />
        )}

        <div className="p-6 space-y-5">
          <div className="space-y-3 pr-10">
            {topContent}
            <Text variant="display-sm" as="h2" id={`confirmation-dialog-title`}>
              {title}
            </Text>
            {description && (
              <Text
                color="subdued"
                variant="body-lg"
                id={`confirmation-dialog-description`}
              >
                {description}
              </Text>
            )}
          </div>
          {mainContent}

          {hasTickConfirmation && (
            <div className="flex items-center gap-2">
              <Checkbox
                onChange={() => setIsConfirmed(!isConfirmed)}
                checked={isConfirmed}
                labelPosition="center"
              >
                {tickConfirmationText}
              </Checkbox>
            </div>
          )}

          <div className="flex space-x-5">
            <Button
              onClick={onCancel}
              color="white"
              size="lg"
              disabled={isLoading}
            >
              {cancelCta}
            </Button>
            <Button
              onClick={onConfirm}
              color="blue"
              size="lg"
              disabled={isLoading || !isConfirmed}
              isLoading={isLoading}
            >
              {confirmCta}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
